import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, Clock, AlertCircle } from 'lucide-react';

const ConstructionGanttChart = ({ milestones, projectStartDate, projectEndDate }) => {
  const constructionStages = [
    { id: 1, name: 'Design & Planning', category: 'Pre-Construction', duration: 14, keywords: ['design', 'planning', 'architectural'] },
    { id: 2, name: 'Approvals & Sanctioning', category: 'Pre-Construction', duration: 21, keywords: ['approval', 'sanction', 'permit'] },
    { id: 3, name: 'Site Preparation', category: 'Site Work', duration: 7, keywords: ['site', 'clearing', 'leveling'] },
    { id: 4, name: 'Excavation', category: 'Foundation', duration: 10, keywords: ['excavation', 'excavate', 'digging'] },
    { id: 5, name: 'Foundation Work', category: 'Foundation', duration: 15, keywords: ['foundation', 'footing'] },
    { id: 6, name: 'Plinth Beam & Column', category: 'Structural', duration: 20, keywords: ['plinth', 'column', 'beam'] },
    { id: 7, name: 'RCC Slab Work', category: 'Structural', duration: 15, keywords: ['slab', 'rcc', 'concrete'] },
    { id: 8, name: 'Masonry & Brickwork', category: 'Structural', duration: 20, keywords: ['masonry', 'brick', 'wall'] },
    { id: 9, name: 'Roofing Work', category: 'Structural', duration: 10, keywords: ['roof', 'roofing', 'terrace'] },
    { id: 10, name: 'Waterproofing', category: 'Protection', duration: 7, keywords: ['waterproof', 'damp'] },
    { id: 11, name: 'Plumbing Rough-in', category: 'MEP', duration: 12, keywords: ['plumbing', 'pipe', 'drainage'] },
    { id: 12, name: 'Electrical Rough-in', category: 'MEP', duration: 12, keywords: ['electrical', 'wiring', 'conduit'] },
    { id: 13, name: 'Plastering', category: 'Finishing', duration: 15, keywords: ['plaster', 'rendering'] },
    { id: 14, name: 'Flooring & Tiling', category: 'Finishing', duration: 15, keywords: ['flooring', 'tile', 'marble'] },
    { id: 15, name: 'Carpentry Work', category: 'Finishing', duration: 20, keywords: ['door', 'window', 'carpentry', 'kitchen'] },
    { id: 16, name: 'False Ceiling', category: 'Finishing', duration: 10, keywords: ['ceiling', 'gypsum', 'pop'] },
    { id: 17, name: 'Painting', category: 'Finishing', duration: 12, keywords: ['paint', 'colour'] },
    { id: 18, name: 'Electrical Finishing', category: 'MEP', duration: 7, keywords: ['switch', 'light', 'fixture'] },
    { id: 19, name: 'Plumbing Fixtures', category: 'MEP', duration: 7, keywords: ['tap', 'sink', 'fixture'] },
    { id: 20, name: 'Sanitary Fittings', category: 'MEP', duration: 5, keywords: ['toilet', 'bathroom', 'sanitary'] },
    { id: 21, name: 'Swimming Pool', category: 'Amenities', duration: 30, keywords: ['pool', 'swimming'], optional: true },
    { id: 22, name: 'Landscaping', category: 'Exterior', duration: 15, keywords: ['landscape', 'garden', 'lawn'] },
    { id: 23, name: 'Hardscaping & Paving', category: 'Exterior', duration: 10, keywords: ['paving', 'hardscape', 'driveway'] },
    { id: 24, name: 'Final Inspection & Handover', category: 'Completion', duration: 7, keywords: ['inspection', 'handover', 'final'] }
  ];

  const mapMilestonesToStages = () => {
    return constructionStages.map(stage => {
      const matchedMilestone = milestones.find(m => {
        const milestoneNameLower = m.stage_name.toLowerCase();
        return stage.keywords.some(keyword => milestoneNameLower.includes(keyword));
      });

      if (matchedMilestone) {
        return {
          ...stage,
          status: matchedMilestone.status,
          expected_start: matchedMilestone.expected_start,
          expected_end: matchedMilestone.expected_end,
          matched: true
        };
      }

      return {
        ...stage,
        status: 'pending',
        matched: false
      };
    }).filter(stage => !stage.optional || stage.matched);
  };

  const stagesWithStatus = mapMilestonesToStages();

  const getStageColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500';
      case 'in_progress':
      case 'review':
        return 'bg-yellow-500';
      default:
        return 'bg-red-400';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="h-4 w-4 text-green-700" />;
      case 'in_progress':
      case 'review':
        return <Clock className="h-4 w-4 text-yellow-700" />;
      default:
        return <AlertCircle className="h-4 w-4 text-red-700" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'in_progress':
        return 'In Progress';
      case 'review':
        return 'Under Review';
      default:
        return 'Upcoming';
    }
  };

  const categories = [...new Set(stagesWithStatus.map(s => s.category))];
  const groupedStages = categories.map(category => ({
    category,
    stages: stagesWithStatus.filter(s => s.category === category)
  }));

  return (
    <Card className="border-stone-200 rounded-sm shadow-sm">
      <CardHeader>
        <CardTitle className="text-2xl font-heading">Construction Progress Timeline</CardTitle>
        <CardDescription>
          Visual representation of your project journey from start to completion
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center space-x-6 mb-6 pb-4 border-b border-stone-200">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-green-500 rounded"></div>
            <span className="text-sm text-stone-600">Completed</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-yellow-500 rounded"></div>
            <span className="text-sm text-stone-600">In Progress</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-red-400 rounded"></div>
            <span className="text-sm text-stone-600">Upcoming</span>
          </div>
        </div>

        <div className="space-y-8">
          {groupedStages.map((group, groupIdx) => (
            <div key={groupIdx} className="space-y-3">
              <h3 className="text-lg font-heading font-semibold text-stone-950 flex items-center">
                <div className="w-1 h-6 bg-orange-700 mr-3"></div>
                {group.category}
              </h3>
              
              <div className="space-y-2">
                {group.stages.map((stage) => (
                  <div key={stage.id} className="flex items-center space-x-4">
                    <div className="w-64 flex items-center space-x-2">
                      {getStatusIcon(stage.status)}
                      <span className="text-sm font-medium text-stone-950">{stage.name}</span>
                    </div>

                    <div className="flex-1">
                      <div className="relative h-8 bg-stone-100 rounded border border-stone-200">
                        <div
                          className={`absolute inset-y-0 left-0 ${getStageColor(stage.status)} rounded flex items-center justify-center transition-all duration-300`}
                          style={{ width: stage.matched ? '100%' : '0%' }}
                        >
                          {stage.matched && stage.expected_start && stage.expected_end && (
                            <span className="text-xs font-medium text-white px-2 truncate">
                              {new Date(stage.expected_start).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })} - {new Date(stage.expected_end).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })}
                            </span>
                          )}
                        </div>
                        {!stage.matched && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-xs text-stone-400">Not Started</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="w-32">
                      <Badge
                        className={`text-xs ${
                          stage.status === 'completed' ? 'bg-green-100 text-green-700' :
                          stage.status === 'in_progress' || stage.status === 'review' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}
                      >
                        {getStatusText(stage.status)}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 pt-6 border-t border-stone-200">
          <div className="grid grid-cols-3 gap-6">
            <div className="text-center">
              <p className="text-sm text-stone-600 mb-1">Project Start</p>
              <p className="text-lg font-heading font-bold text-stone-950">
                {new Date(projectStartDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-stone-600 mb-1">Current Progress</p>
              <p className="text-lg font-heading font-bold text-orange-700">
                {Math.round((stagesWithStatus.filter(s => s.status === 'completed').length / stagesWithStatus.length) * 100)}%
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-stone-600 mb-1">Expected Completion</p>
              <p className="text-lg font-heading font-bold text-stone-950">
                {new Date(projectEndDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ConstructionGanttChart;
